import { useI18n } from '../i18n';
import { Icon } from '../ui/Icon';
import { formatMs } from './EnginePill';
import type { CompileUi, EngineUi } from './engineUi';

export function StatusBar({
  engine,
  compile,
  schemaVersion,
  engineVersion,
  isMock,
  onAbout,
}: {
  engine: EngineUi;
  compile: CompileUi;
  schemaVersion: string | null;
  engineVersion: string | null;
  isMock: boolean;
  onAbout: () => void;
}) {
  const { t, lang } = useI18n();
  let dot = 'bg-subtle';
  let state: string;
  if (engine.status === 'ready') {
    dot = isMock ? 'bg-warning' : 'bg-accent';
    state = `${t('engine.readyVersion', { version: engine.info.engineVersion })}${isMock ? ` · ${t('engine.mock')}` : ''}`;
  } else if (engine.status === 'unavailable') {
    dot = 'bg-error';
    state = t('engine.unavailable');
  } else {
    dot = 'bg-warning animate-pulse motion-reduce:animate-none';
    const p = engine.progress;
    state = `${t(`engine.stage.${p?.stage ?? 'downloading-runtime'}`)}${p?.percent !== undefined ? ` ${p.percent} %` : '…'}`;
    if (lang === 'en') state = state.replace(/ (\d+) %$/, ' $1%');
  }
  return (
    <footer className="flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-line bg-bg px-4 py-2 text-2xs text-muted sm:px-6" data-testid="status-bar">
      <span className="inline-flex items-center gap-2">
        <span className={`h-1.5 w-1.5 rounded-pill ${dot}`} aria-hidden="true" />
        <span data-testid="engine-state">{state}</span>
      </span>
      {compile.status === 'done' && <span className="tabular">{t('engine.compiled', { ms: formatMs(compile.ms, lang) })}</span>}
      {(schemaVersion || engineVersion) && (
        <span className="tabular text-subtle">
          {schemaVersion && t('status.schema', { v: schemaVersion })}
          {schemaVersion && engineVersion && ' · '}
          {engineVersion && t('status.engine', { v: engineVersion })}
        </span>
      )}
      <span className="tabular text-subtle" data-testid="build-stamp" title={t('status.buildTitle')}>
        {t('status.build', { sha: __BUILD_SHA__, date: __BUILD_DATE__ })}
      </span>
      <span className="ml-auto inline-flex items-center gap-2 font-semibold text-fg">
        <Icon name="lock" size={12} className="text-accent-strong" />
        {t('status.privacy')}
      </span>
      <button type="button" onClick={onAbout} className="rounded px-1 font-semibold text-muted underline decoration-line-strong underline-offset-4 hover:text-fg">
        {t('status.about')}
      </button>
    </footer>
  );
}
